// Samenvatting van de andere velden van een activiteit, per punt in een
// outputgrafiek (updateverzoek Team Sportclub, 2026-10-14).
//
// Een punt is een datum: de grafiek groepeert output-regels op
// `toLocaleDateString('nl-NL')` van hun Timestamp (useOutputData). De velden
// horen bij dezelfde registraties, en die herken je aan de gedeelde
// `SessionID`. We groeperen dus op de datum van de OUTPUT-regel en zoeken de
// veldantwoorden erbij via SessionID — niet op de eigen datum van het
// veldantwoord, die kan een paar seconden of een dag afwijken.
//
// Per punt alleen de registraties van die datum, ook in een lijngrafiek die
// optelt over de tijd (besluit user 2026-10-14): zo zie je per bijeenkomst
// wat er gebeurde.

import { toDateKey } from './toJsDate'

/**
 * @typedef {object} EventFieldDef
 * @property {string} ID
 * @property {string} [Title]
 * @property {string} Type - EventFields.Type
 * @property {number} [Position]
 */

/**
 * @typedef {object} FieldResult
 * @property {EventFieldDef} field - Het veld zoals het in de grafiek-organisatie heet
 * @property {Array<{SessionID?: string, Value?: *}>} records - Alle antwoorden op dat
 *   veld, ook die uit gekoppelde projecten (die dragen daar een eigen FieldID)
 */

/**
 * @typedef {object} FieldSummaryLine
 * @property {string} label - Titel van het veld
 * @property {string} value - Samenvatting, bijv. '3', '7,5 (gem.)', 'Binnen 2 · Buiten 1'
 */

/** Veldsoorten die in een tooltip passen, en hoe ze samengevat worden. */
const SUMMARIZERS = {
    number: 'sum',
    scale: 'average',
    'multiple-one': 'count',
    'multiple-multiple': 'count',
}

/**
 * De veldsoorten die de tooltip toont. Eén lijst voor het ophalen
 * (useOutputFieldResults) en de openbare poort op de server
 * (functions/liveReports/publicReportDataAccess.js, pariteitstest
 * outputTooltipFieldTypes.parity.test.js). Open tekst staat er bewust niet in.
 */
export const TOOLTIP_FIELD_TYPES = Object.keys(SUMMARIZERS)

// AI-CONTEXT: See docs/pitfalls.md#security-free-text-hides-in-a-choice-answer
/** Wat er staat bij een 'Anders, namelijk'-antwoord: nooit de getypte tekst. */
export const OTHER_CHOICE_LABEL = 'Anders'

/**
 * De naam van een keuze-optie zoals de tooltip hem toont. De 'Anders,
 * namelijk'-optie heet in MultipleQuestionOptions letterlijk 'other'.
 * Gespiegeld op de server (publicReportDataAccess.js#choiceLabel), gepind door
 * outputTooltipFieldTypes.parity.test.js.
 *
 * @param {{ Option?: string }|undefined} option
 * @returns {string|null} null als de optie onbekend is
 */
export const choiceLabel = (option) => {
    if (!option || typeof option.Option !== 'string' || option.Option.trim() === '') return null
    return option.Option === 'other' ? OTHER_CHOICE_LABEL : option.Option
}

/**
 * Zet bij keuzeantwoorden de naam van de optie in Value, in plaats van wat er
 * bij het antwoord is opgeslagen. Bij 'Anders, namelijk' staat daar de vrije
 * tekst van de invuller ('Jan de Vries, Kerkstraat 3'), en een registratie
 * draagt geen OptionType — je herkent hem alleen aan de optie (/complete
 * 2026-10-14). Een antwoord zonder bekende optie valt weg: dan is niet te
 * zeggen of de opgeslagen tekst een optienaam of vrije tekst is.
 * Bijkomend: een hernoemde optie telt onder zijn huidige naam.
 *
 * @param {Array<{ OptionID?: string, Value?: * }>} records - Antwoorden op één keuzeveld
 * @param {Object<string, { Option?: string }>} optionsById - MultipleQuestionOptions op ID
 * @returns {Array<object>}
 */
export const normalizeChoiceRecords = (records, optionsById) => (records || [])
    .map(r => ({ record: r, label: choiceLabel(optionsById?.[r?.OptionID]) }))
    .filter(({ label }) => label !== null)
    .map(({ record, label }) => ({ ...record, Value: label }))

/**
 * @param {number} n
 * @returns {string} Nederlands, hooguit één decimaal: 3 → '3', 7.5 → '7,5'
 */
const formatNumber = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1).replace('.', ','))

/**
 * @param {*} value
 * @returns {number|null} null voor alles wat geen getal is — een leeg antwoord
 *   is 'niets ingevuld', niet 0
 */
const toNumber = (value) => {
    if (value === '' || value === null || value === undefined) return null
    const n = Number(value)
    return Number.isFinite(n) ? n : null
}

/**
 * @param {'sum'|'average'|'count'} kind
 * @param {Array<*>} values - De antwoorden van één veld op één datum
 * @returns {string|null} null als er niets samen te vatten valt
 */
const summarize = (kind, values) => {
    if (kind === 'count') {
        const counts = new Map()
        values
            .filter(v => typeof v === 'string' && v.trim() !== '')
            .forEach(v => counts.set(v, (counts.get(v) || 0) + 1))
        if (counts.size === 0) return null
        // Meest gekozen eerst; bij gelijkspel op naam, zodat de volgorde niet
        // van de volgorde van de documenten afhangt.
        return [...counts.entries()]
            .sort((a, b) => (b[1] - a[1]) || a[0].localeCompare(b[0]))
            .map(([option, count]) => `${option} ${count}`)
            .join(' · ')
    }

    const numbers = values.map(toNumber).filter(n => n !== null)
    if (numbers.length === 0) return null
    const sum = numbers.reduce((acc, n) => acc + n, 0)
    return kind === 'sum'
        ? formatNumber(sum)
        : `${formatNumber(sum / numbers.length)} (gem.)`
}

/**
 * @param {object} args
 * @param {Array<{SessionID?: string, Timestamp?: *}>} args.outputRecords - De
 *   output-regels achter de grafiek (al gefilterd op periode en deelnemer)
 * @param {FieldResult[]} args.fieldResults
 * @returns {Object<string, FieldSummaryLine[]>} Per datum ('12-5-2026', dezelfde
 *   sleutel als de grafiek) de regels voor de tooltip; een datum zonder iets
 *   samen te vatten ontbreekt
 */
const summarizeOutputFields = ({ outputRecords, fieldResults }) => {
    /** @type {Map<string, Set<string>>} */
    const sessionsByDate = new Map()
    ;(outputRecords || []).forEach((r) => {
        const key = toDateKey(r?.Timestamp)
        if (!key || !r.SessionID) return
        if (!sessionsByDate.has(key)) sessionsByDate.set(key, new Set())
        sessionsByDate.get(key).add(r.SessionID)
    })

    const fields = (fieldResults || [])
        .filter(fr => SUMMARIZERS[fr?.field?.Type])
        .sort((a, b) => ((Number(a.field.Position) || 0) - (Number(b.field.Position) || 0))
            || String(a.field.ID).localeCompare(String(b.field.ID)))

    /** @type {Object<string, FieldSummaryLine[]>} */
    const byDate = {}
    sessionsByDate.forEach((sessions, key) => {
        const lines = fields
            .map(({ field, records }) => ({
                label: field.Title || 'Veld zonder naam',
                value: summarize(
                    SUMMARIZERS[field.Type],
                    (records || []).filter(r => sessions.has(r.SessionID)).map(r => r.Value),
                ),
            }))
            .filter(line => line.value !== null)
        if (lines.length > 0) byDate[key] = lines
    })

    return byDate
}

export default summarizeOutputFields
